<template>
    <div v-if="visible" class="psm-backdrop" @click.self="close">
        <div class="psm-dialog" role="dialog" :aria-label="title">
            <header class="psm-header">
                <div class="psm-title-wrap">
                    <span class="psm-title">
                        <i v-if="titleIconClass" :class="titleIconClass"></i>
                        {{ title }}
                    </span>
                    <span v-if="subtitle" class="psm-subtitle">{{ subtitle }}</span>
                </div>
                <button type="button" class="psm-close" aria-label="닫기" @click="close">
                    <i class="fas fa-times"></i>
                </button>
            </header>

            <div class="psm-search-area">
                <div class="psm-search">
                    <input
                        ref="keywordInput"
                        class="psm-keyword-input"
                        :value="keyword"
                        type="text"
                        :placeholder="searchPlaceholder"
                        @input="onKeywordInput($event.target.value)"
                        @keyup.enter="onSearchClick"
                    />
                    <button type="button" class="psm-search-btn" :disabled="loading" @click="onSearchClick">
                        <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                        <i v-else class="fas fa-search"></i>
                        {{ searchButtonLabel }}
                    </button>
                </div>
                <div v-if="resolvedQuickFilters.length" class="psm-quick-filters" aria-label="빠른 그룹 필터">
                    <button
                        v-for="filter in resolvedQuickFilters"
                        :key="`psm-qf-${filter.value}`"
                        type="button"
                        class="psm-quick-chip"
                        :class="{ 'psm-quick-chip--active': isQuickFilterActive(filter.value) }"
                        @click="onQuickFilterClick(filter.value)"
                    >
                        {{ filter.label }}
                    </button>
                </div>
            </div>

            <div class="psm-body">
                <div v-if="loading" class="psm-state psm-state--loading">
                    <i class="fas fa-spinner fa-spin"></i>
                    {{ loadingLabel }}
                </div>
                <div v-else-if="isEmptyDataset" class="psm-state psm-state--empty">
                    <i class="fas fa-inbox"></i>
                    {{ emptyNoDataMessage }}
                </div>
                <div v-else-if="!rows.length" class="psm-state psm-state--empty">
                    <i class="fas fa-inbox"></i>
                    {{ emptyFilteredMessage }}
                </div>
                <table v-else class="psm-table">
                    <thead>
                        <tr>
                            <th v-for="col in resolvedColumns" :key="col.field">{{ col.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, idx) in rows"
                            :key="rowKey(row, idx)"
                            class="psm-row"
                            @click="onRowClick(row)"
                        >
                            <td
                                v-for="col in resolvedColumns"
                                :key="col.field"
                                :class="{ 'psm-cell--emphasis': col.emphasis }"
                            >
                                {{ cellText(row, col) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <footer class="psm-footer">
                <span v-if="showFooterCount" class="psm-result-count">{{ footerCountText }}</span>
                <button type="button" class="psm-cancel-btn" @click="close">{{ closeButtonLabel }}</button>
            </footer>
        </div>
    </div>
</template>

<script>
const DEFAULT_COLUMNS = [
    { field: "ptAddr", label: "관제점 주소", emphasis: true },
    { field: "ptName", label: "관제점명" },
    { field: "ptGroup", label: "그룹" },
];

/**
 * 관제점 검색 모달 — 생산/에너지 등 용도별로 props·컬럼만 넘기면 동일 UI로 재사용.
 */
export default {
    name: "PointSearchModal",
    props: {
        visible: { type: Boolean, default: false },
        /** v-model (sync) 용 검색어 */
        keyword: { type: String, default: "" },
        loading: { type: Boolean, default: false },
        /** 필터 적용 후 표시할 행 */
        rows: { type: Array, default: () => [] },
        /**
         * 전체 후보 건수(필터 전). 0이면 목록 없음으로 간주.
         * null이면 푸터 건수 영역 숨김.
         */
        listTotalCount: { type: Number, default: null },
        title: { type: String, required: true },
        subtitle: { type: String, default: "" },
        /** 예: 'fas fa-search', 'fas fa-bolt' */
        titleIconClass: { type: String, default: "fas fa-search" },
        searchPlaceholder: { type: String, default: "주소·이름으로 필터 (비우고 조회 시 전체)" },
        searchButtonLabel: { type: String, default: "조회" },
        closeButtonLabel: { type: String, default: "닫기" },
        loadingLabel: { type: String, default: "검색 중..." },
        emptyNoDataMessage: { type: String, default: "조건에 맞는 관제점이 없습니다." },
        emptyFilteredMessage: { type: String, default: "검색 결과가 없습니다." },
        /**
         * 테이블 컬럼 [{ field, label, emphasis? }]
         * field 는 행 객체의 키(평면). 중첩이 필요하면 나중에 cellFormatter 슬롯 확장 가능.
         */
        columns: {
            type: Array,
            default: null,
        },
        /** 빠른 필터 뱃지: ['ENSPLY'] 또는 [{ value, label }] */
        quickFilters: { type: Array, default: () => [] },
        /** 현재 선택된 빠른 필터 값 */
        activeQuickFilter: { type: String, default: "" },
    },
    computed: {
        resolvedColumns() {
            return Array.isArray(this.columns) && this.columns.length ? this.columns : DEFAULT_COLUMNS;
        },
        resolvedQuickFilters() {
            if (!Array.isArray(this.quickFilters) || !this.quickFilters.length) return [];
            const mapped = this.quickFilters
                .map((item) => {
                    if (typeof item === "string") {
                        const value = item.trim();
                        return value ? { value, label: value } : null;
                    }
                    if (!item || typeof item !== "object") return null;
                    const value = String(item.value ?? item.code ?? item.group ?? "").trim();
                    if (!value) return null;
                    const label = String(item.label ?? item.name ?? value).trim() || value;
                    return { value, label };
                })
                .filter(Boolean);
            const seen = new Set();
            return mapped.filter((item) => {
                const key = String(item.value || "").trim().toUpperCase();
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        },
        isEmptyDataset() {
            return this.listTotalCount !== null && this.listTotalCount <= 0;
        },
        showFooterCount() {
            return !this.loading && this.listTotalCount != null && this.listTotalCount > 0;
        },
        footerCountText() {
            return `표시 ${this.rows.length} / 전체 ${this.listTotalCount}건`;
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.$nextTick(() => this.$refs.keywordInput?.focus());
            }
        },
    },
    methods: {
        close() {
            this.$emit("update:visible", false);
            this.$emit("close");
        },
        onKeywordInput(value) {
            this.$emit("update:keyword", value);
        },
        onSearchClick() {
            this.$emit("search");
        },
        onQuickFilterClick(value) {
            this.$emit("quick-filter", value);
        },
        isQuickFilterActive(value) {
            const current = String(this.activeQuickFilter || "")
                .trim()
                .toUpperCase();
            const target = String(value || "")
                .trim()
                .toUpperCase();
            return !!current && current === target;
        },
        onRowClick(row) {
            this.$emit("select", row);
        },
        rowKey(row, idx) {
            if (row && row.ptIdx != null) return `psm-${row.ptIdx}-${idx}`;
            const addr = row && row.ptAddr != null ? String(row.ptAddr) : "";
            return `psm-${addr}-${idx}`;
        },
        cellText(row, col) {
            if (!row || !col || !col.field) return "";
            const v = row[col.field];
            return v != null && v !== "" ? String(v) : "";
        },
    },
};
</script>

<style scoped>
.psm-backdrop {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.4);
    z-index: 9999;
}

.psm-dialog {
    width: 620px;
    max-width: calc(100vw - 24px);
    max-height: min(82vh, 760px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 14px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: #fff;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
}

.psm-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px 10px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.96);
}

.psm-title-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
}

.psm-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
}

.psm-title i {
    color: var(--puc-blue-strong, #2563eb);
}

.psm-subtitle {
    font-size: 10px;
    font-weight: 600;
    color: var(--puc-slate-500, #64748b);
    letter-spacing: 0.02em;
    line-height: 1.35;
}

.psm-close {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: rgba(241, 245, 249, 0.94);
    color: var(--puc-slate-500, #64748b);
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.psm-close:hover {
    background: rgba(226, 232, 240, 0.96);
    color: var(--puc-navy, #1b263b);
}

.psm-search {
    display: flex;
    gap: 8px;
    padding: 10px 14px;
}

.psm-search-area {
    border-bottom: 1px solid rgba(241, 245, 249, 0.96);
}

.psm-keyword-input {
    flex: 1;
    min-width: 0;
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 9px;
    padding: 8px 10px;
    font-size: 12px;
    color: var(--puc-navy, #1b263b);
    outline: none;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.psm-keyword-input:focus {
    border-color: rgba(37, 99, 235, 0.66);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.psm-search-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 34px;
    padding: 8px 12px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(180deg, #1d7efc, #0069e0);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.psm-search-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.psm-quick-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 14px 10px;
}

.psm-quick-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 9px;
    border: 1px solid rgba(148, 163, 184, 0.46);
    border-radius: 999px;
    background: #fff;
    color: #475569;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;
}

.psm-quick-chip:hover {
    border-color: rgba(15, 118, 110, 0.55);
    color: #0f766e;
}

.psm-quick-chip--active {
    border-color: rgba(15, 118, 110, 0.52);
    background: rgba(15, 118, 110, 0.08);
    color: #0f766e;
}

.psm-body {
    flex: 1;
    min-height: 140px;
    overflow: auto;
}

.psm-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 160px;
    padding: 14px;
    color: var(--puc-slate-500, #64748b);
    font-size: 12px;
}

.psm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.psm-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 8px 12px;
    background: rgba(248, 250, 252, 0.98);
    border-bottom: 1px solid rgba(226, 232, 240, 0.96);
    color: var(--puc-slate-500, #64748b);
    font-size: 10px;
    font-weight: 700;
    text-align: center;
}

.psm-row {
    cursor: pointer;
    transition: background 0.16s ease;
}

.psm-row:hover {
    background: rgba(239, 246, 255, 0.6);
}

.psm-row td {
    padding: 8px 14px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.94);
    color: var(--puc-slate-700, #475569);
    text-align: center;
    font-size: 11px;
}

.psm-cell--emphasis {
    color: var(--puc-navy, #1b263b);
}

.psm-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid rgba(241, 245, 249, 0.96);
    background: rgba(248, 250, 252, 0.72);
}

.psm-result-count {
    font-size: 11px;
    font-weight: 700;
    color: var(--puc-slate-500, #64748b);
}

.psm-cancel-btn {
    min-height: 32px;
    padding: 6px 12px;
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    color: var(--puc-slate-700, #475569);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}
</style>
